
import { JavaScriptCourse } from './types';

export const JS_FUNDAMENTALS_COURSE: JavaScriptCourse = {
  id: 'js-fundamentals',
  title: 'JavaScript Fundamentals',
  description: 'Master the core concepts of JavaScript programming from scratch. Perfect for beginners starting their web development journey.',
  level: 'fundamentals',
  totalDuration: '8-10 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Basic computer literacy', 'Understanding of HTML/CSS basics'],
  learningOutcomes: [
    'Understand JavaScript syntax and data types',
    'Master control structures and functions',
    'Manipulate the DOM effectively',
    'Handle events and user interactions',
    'Debug JavaScript code confidently',
    'Apply ES6+ features in practice'
  ],
  modules: [
    {
      id: 'js-basics',
      title: 'JavaScript Basics & Syntax',
      description: 'Introduction to JavaScript variables, data types, and operators',
      duration: '60 minutes',
      difficulty: 'beginner',
      objectives: [
        'Understand what JavaScript is and how it works',
        'Learn about variables and data types',
        'Master operators and expressions',
        'Practice with console output'
      ],
      content: `# JavaScript Basics & Syntax

## What is JavaScript?

JavaScript is a powerful, versatile programming language that runs in web browsers and servers. It's the language that makes web pages interactive and dynamic.

## Variables and Data Types

### Declaring Variables

\`\`\`javascript
// Modern way (ES6+)
let name = "John";
const age = 25;

// Older way (avoid in modern code)
var city = "New York";
\`\`\`

### Data Types

JavaScript has several primitive data types:

\`\`\`javascript
// String
let message = "Hello, World!";
let template = \`Hello, \${name}!\`;

// Number
let integer = 42;
let decimal = 3.14;

// Boolean
let isActive = true;
let isComplete = false;

// Undefined
let undefined_var;

// Null
let empty = null;
\`\`\`

## Operators

### Arithmetic Operators
\`\`\`javascript
let a = 10;
let b = 3;

console.log(a + b); // 13
console.log(a - b); // 7
console.log(a * b); // 30
console.log(a / b); // 3.333...
console.log(a % b); // 1 (remainder)
\`\`\`

### Comparison Operators
\`\`\`javascript
console.log(5 == "5");  // true (loose equality)
console.log(5 === "5"); // false (strict equality)
console.log(10 > 5);    // true
console.log(3 <= 3);    // true
\`\`\`

## Practice Exercise

Try creating variables for your personal information and display them using template literals.`,
      codeExamples: [
        {
          title: 'Variable Declaration',
          language: 'javascript',
          code: `let firstName = "Alice";
const birthYear = 1990;
let currentYear = 2024;
let age = currentYear - birthYear;

console.log(\`Hello, \${firstName}! You are \${age} years old.\`);`,
          explanation: 'This example shows how to declare variables and use template literals for string interpolation.'
        }
      ],
      practice: {
        task: 'Create a simple calculator that performs basic arithmetic operations',
        starter: `// Create variables for two numbers
let num1 = 10;
let num2 = 5;

// Perform calculations and store results
// Add your code here`,
        solution: `let num1 = 10;
let num2 = 5;

let sum = num1 + num2;
let difference = num1 - num2;
let product = num1 * num2;
let quotient = num1 / num2;

console.log(\`Sum: \${sum}\`);
console.log(\`Difference: \${difference}\`);
console.log(\`Product: \${product}\`);
console.log(\`Quotient: \${quotient}\`);`,
        hints: [
          'Use arithmetic operators (+, -, *, /) to perform calculations',
          'Store results in new variables',
          'Use template literals to display results'
        ]
      },
      quiz: {
        questions: [
          {
            id: 'js-basics-1',
            question: 'Which keyword is used to declare a constant variable in JavaScript?',
            options: ['var', 'let', 'const', 'final'],
            correctAnswer: 2,
            difficulty: 'easy',
            explanation: 'The const keyword is used to declare constants that cannot be reassigned.',
            points: 10
          },
          {
            id: 'js-basics-2',
            question: 'What is the result of 5 === "5" in JavaScript?',
            options: ['true', 'false', 'undefined', 'error'],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'The === operator checks for strict equality, comparing both value and type. 5 (number) is not strictly equal to "5" (string).',
            points: 15
          }
        ],
        passThreshold: 75
      }
    },
    {
      id: 'js-control-structures',
      title: 'Control Structures',
      description: 'Master conditionals, loops, and decision-making in JavaScript',
      duration: '75 minutes',
      difficulty: 'beginner',
      objectives: [
        'Use if/else statements effectively',
        'Implement various types of loops',
        'Apply switch statements',
        'Understand break and continue'
      ],
      content: `# Control Structures

## Conditional Statements

### If/Else Statements

\`\`\`javascript
let temperature = 25;

if (temperature > 30) {
    console.log("It's hot!");
} else if (temperature > 20) {
    console.log("It's warm.");
} else {
    console.log("It's cool.");
}
\`\`\`

### Ternary Operator

\`\`\`javascript
let age = 18;
let status = age >= 18 ? "adult" : "minor";
console.log(status); // "adult"
\`\`\`

## Loops

### For Loop

\`\`\`javascript
// Traditional for loop
for (let i = 0; i < 5; i++) {
    console.log(\`Number: \${i}\`);
}

// For...of loop (for arrays)
let colors = ["red", "green", "blue"];
for (let color of colors) {
    console.log(color);
}
\`\`\`

### While Loop

\`\`\`javascript
let count = 0;
while (count < 3) {
    console.log(\`Count: \${count}\`);
    count++;
}
\`\`\`

## Switch Statements

\`\`\`javascript
let day = "Monday";

switch (day) {
    case "Monday":
        console.log("Start of work week");
        break;
    case "Friday":
        console.log("TGIF!");
        break;
    case "Saturday":
    case "Sunday":
        console.log("Weekend!");
        break;
    default:
        console.log("Regular day");
}
\`\`\``,
      practice: {
        task: 'Create a number guessing game using loops and conditionals',
        starter: `// Number guessing game
let targetNumber = 7;
let guess = 0;
let attempts = 0;

// Add your game logic here`,
        hints: [
          'Use a while loop to keep the game running',
          'Increment attempts counter',
          'Use if/else to check if guess is correct, too high, or too low',
          'Break the loop when correct guess is made'
        ]
      },
      quiz: {
        questions: [
          {
            id: 'js-control-1',
            question: 'What will be the output of: for(let i = 0; i < 3; i++) { console.log(i); }',
            options: ['0 1 2', '1 2 3', '0 1 2 3', 'Error'],
            correctAnswer: 0,
            difficulty: 'easy',
            explanation: 'The loop starts at 0, runs while i < 3, so it prints 0, 1, 2.',
            points: 10
          }
        ],
        passThreshold: 75
      }
    },
    {
      id: 'js-functions',
      title: 'Functions & Scope',
      description: 'Learn to create and use functions, understand scope and closures',
      duration: '90 minutes',
      difficulty: 'beginner',
      objectives: [
        'Create function declarations and expressions',
        'Understand function parameters and return values',
        'Master scope and hoisting concepts',
        'Use arrow functions effectively'
      ],
      content: `# Functions & Scope

## Function Declarations

\`\`\`javascript
// Function declaration
function greet(name) {
    return \`Hello, \${name}!\`;
}

// Function expression
const greetExpression = function(name) {
    return \`Hello, \${name}!\`;
};

// Arrow function (ES6+)
const greetArrow = (name) => \`Hello, \${name}!\`;

// Calling functions
console.log(greet("Alice"));
console.log(greetExpression("Bob"));
console.log(greetArrow("Charlie"));
\`\`\`

## Parameters and Return Values

\`\`\`javascript
// Default parameters
function createUser(name, age = 18, role = "user") {
    return {
        name: name,
        age: age,
        role: role
    };
}

// Rest parameters
function sum(...numbers) {
    return numbers.reduce((total, num) => total + num, 0);
}

console.log(sum(1, 2, 3, 4)); // 10
\`\`\`

## Scope

\`\`\`javascript
// Global scope
let globalVar = "I'm global";

function demonstrateScope() {
    // Function scope
    let functionVar = "I'm in function scope";
    
    if (true) {
        // Block scope
        let blockVar = "I'm in block scope";
        console.log(globalVar);    // Accessible
        console.log(functionVar);  // Accessible
        console.log(blockVar);     // Accessible
    }
    
    // console.log(blockVar); // Error! Not accessible
}
\`\`\`

## Arrow Functions

\`\`\`javascript
// Traditional function
const multiply = function(a, b) {
    return a * b;
};

// Arrow function - single line
const multiplyArrow = (a, b) => a * b;

// Arrow function - multiple lines
const calculate = (a, b, operation) => {
    if (operation === 'add') {
        return a + b;
    } else if (operation === 'multiply') {
        return a * b;
    }
    return 0;
};
\`\`\``,
      practice: {
        task: 'Create a function library for common mathematical operations',
        starter: `// Create functions for basic math operations
// add, subtract, multiply, divide

// Add your functions here`,
        solution: `// Mathematical operations library
const add = (a, b) => a + b;
const subtract = (a, b) => a - b;
const multiply = (a, b) => a * b;
const divide = (a, b) => b !== 0 ? a / b : "Cannot divide by zero";

// Higher-order function
const calculate = (operation, a, b) => {
    switch(operation) {
        case 'add': return add(a, b);
        case 'subtract': return subtract(a, b);
        case 'multiply': return multiply(a, b);
        case 'divide': return divide(a, b);
        default: return "Invalid operation";
    }
};

// Test the functions
console.log(calculate('add', 5, 3));      // 8
console.log(calculate('multiply', 4, 7)); // 28`,
        hints: [
          'Use arrow functions for simple operations',
          'Handle edge cases like division by zero',
          'Create a higher-order function that takes operation as parameter',
          'Use switch statement for operation selection'
        ]
      },
      quiz: {
        questions: [
          {
            id: 'js-functions-1',
            question: 'What is the main difference between function declarations and arrow functions?',
            options: [
              'Arrow functions are faster',
              'Arrow functions don\'t have their own "this" context',
              'Function declarations can\'t have parameters',
              'No difference'
            ],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'Arrow functions do not have their own "this" context and inherit it from the enclosing scope.',
            points: 15
          }
        ],
        passThreshold: 75
      }
    },
    {
      id: 'js-arrays-objects',
      title: 'Arrays & Objects',
      description: 'Master JavaScript data structures and their manipulation methods',
      duration: '85 minutes',
      difficulty: 'beginner',
      objectives: [
        'Create and manipulate arrays',
        'Use array methods effectively',
        'Work with objects and their properties',
        'Understand object destructuring'
      ],
      content: `# Arrays & Objects

## Arrays

### Creating and Accessing Arrays

\`\`\`javascript
// Creating arrays
let fruits = ["apple", "banana", "orange"];
let numbers = [1, 2, 3, 4, 5];
let mixed = ["hello", 42, true, null];

// Accessing elements
console.log(fruits[0]);        // "apple"
console.log(fruits.length);    // 3
console.log(fruits[fruits.length - 1]); // "orange"
\`\`\`

### Array Methods

\`\`\`javascript
let numbers = [1, 2, 3, 4, 5];

// Adding elements
numbers.push(6);           // Add to end
numbers.unshift(0);        // Add to beginning

// Removing elements
let last = numbers.pop();   // Remove from end
let first = numbers.shift(); // Remove from beginning

// Finding elements
let index = numbers.indexOf(3);
let found = numbers.find(num => num > 3);
let exists = numbers.includes(4);

// Transforming arrays
let doubled = numbers.map(num => num * 2);
let evens = numbers.filter(num => num % 2 === 0);
let sum = numbers.reduce((total, num) => total + num, 0);
\`\`\`

## Objects

### Creating and Accessing Objects

\`\`\`javascript
// Object literal
let person = {
    name: "John",
    age: 30,
    city: "New York",
    isStudent: false
};

// Accessing properties
console.log(person.name);        // "John"
console.log(person["age"]);      // 30

// Adding/modifying properties
person.email = "john@email.com";
person.age = 31;

// Deleting properties
delete person.isStudent;
\`\`\`

### Object Methods

\`\`\`javascript
let person = {
    firstName: "John",
    lastName: "Doe",
    fullName: function() {
        return this.firstName + " " + this.lastName;
    },
    // ES6 method syntax
    greet() {
        return \`Hello, I'm \${this.fullName()}\`;
    }
};

console.log(person.greet()); // "Hello, I'm John Doe"
\`\`\`

### Destructuring

\`\`\`javascript
// Array destructuring
let [first, second, third] = ["red", "green", "blue"];

// Object destructuring
let {name, age, city} = person;
console.log(name); // "John"

// With default values
let {email = "no email"} = person;
\`\`\``,
      practice: {
        task: 'Create a student management system using arrays and objects',
        starter: `// Student management system
let students = [];

// Add functions to:
// 1. Add a student
// 2. Find a student by name
// 3. Calculate average grade
// 4. Get students by grade level

// Add your code here`,
        solution: `let students = [];

// Add student function
function addStudent(name, age, grade, subjects = []) {
    const student = {
        id: students.length + 1,
        name: name,
        age: age,
        grade: grade,
        subjects: subjects,
        createdAt: new Date()
    };
    students.push(student);
    return student;
}

// Find student by name
function findStudent(name) {
    return students.find(student => 
        student.name.toLowerCase() === name.toLowerCase()
    );
}

// Calculate average grade
function calculateAverageGrade() {
    if (students.length === 0) return 0;
    const totalGrade = students.reduce((sum, student) => sum + student.grade, 0);
    return totalGrade / students.length;
}

// Get students by grade level
function getStudentsByGrade(targetGrade) {
    return students.filter(student => student.grade === targetGrade);
}

// Test the system
addStudent("Alice", 16, 95, ["Math", "Science"]);
addStudent("Bob", 17, 88, ["English", "History"]);
addStudent("Charlie", 16, 92, ["Math", "Art"]);

console.log("All students:", students);
console.log("Found Alice:", findStudent("Alice"));
console.log("Average grade:", calculateAverageGrade());
console.log("Grade 16 students:", getStudentsByGrade(16));`,
        hints: [
          'Use objects to represent students with properties like name, age, grade',
          'Store students in an array for easy manipulation',
          'Use array methods like find(), filter(), and reduce()',
          'Consider adding unique IDs to each student'
        ]
      }
    },
    {
      id: 'js-dom-manipulation',
      title: 'DOM Manipulation',
      description: 'Learn to interact with HTML elements using JavaScript',
      duration: '90 minutes',
      difficulty: 'beginner',
      objectives: [
        'Select HTML elements',
        'Modify element content and attributes',
        'Handle events effectively',
        'Create dynamic web pages'
      ],
      content: `# DOM Manipulation

## What is the DOM?

The Document Object Model (DOM) is a programming interface for HTML documents. It represents the page structure as a tree of objects that JavaScript can manipulate.

## Selecting Elements

\`\`\`javascript
// By ID
let header = document.getElementById('main-header');

// By class name
let buttons = document.getElementsByClassName('btn');

// By tag name
let paragraphs = document.getElementsByTagName('p');

// CSS selectors (modern approach)
let firstButton = document.querySelector('.btn');
let allButtons = document.querySelectorAll('.btn');
let specificElement = document.querySelector('#main-header .title');
\`\`\`

## Modifying Elements

\`\`\`javascript
// Changing content
let element = document.querySelector('#my-element');
element.textContent = 'New text content';
element.innerHTML = '<strong>Bold text</strong>';

// Changing attributes
element.setAttribute('class', 'new-class');
element.id = 'new-id';
element.style.color = 'blue';
element.style.backgroundColor = 'yellow';

// CSS classes
element.classList.add('active');
element.classList.remove('inactive');
element.classList.toggle('highlight');
element.classList.contains('active'); // returns boolean
\`\`\`

## Creating and Adding Elements

\`\`\`javascript
// Create new element
let newDiv = document.createElement('div');
newDiv.textContent = 'I am a new div';
newDiv.className = 'new-element';

// Add to the page
let container = document.querySelector('#container');
container.appendChild(newDiv);

// Insert at specific position
let referenceElement = document.querySelector('#reference');
container.insertBefore(newDiv, referenceElement);
\`\`\`

## Event Handling

\`\`\`javascript
// Click event
let button = document.querySelector('#my-button');
button.addEventListener('click', function(event) {
    console.log('Button was clicked!');
    console.log('Event object:', event);
});

// Multiple event types
let input = document.querySelector('#my-input');

input.addEventListener('focus', function() {
    console.log('Input focused');
});

input.addEventListener('blur', function() {
    console.log('Input lost focus');
});

input.addEventListener('input', function(event) {
    console.log('Input value:', event.target.value);
});

// Form submission
let form = document.querySelector('#my-form');
form.addEventListener('submit', function(event) {
    event.preventDefault(); // Prevent default form submission
    console.log('Form submitted');
});
\`\`\`

## Practical Example: Interactive List

\`\`\`javascript
// HTML structure needed:
// <input type="text" id="item-input">
// <button id="add-button">Add Item</button>
// <ul id="item-list"></ul>

let input = document.getElementById('item-input');
let addButton = document.getElementById('add-button');
let itemList = document.getElementById('item-list');

function addItem() {
    let itemText = input.value.trim();
    
    if (itemText === '') {
        alert('Please enter an item');
        return;
    }
    
    // Create list item
    let listItem = document.createElement('li');
    listItem.textContent = itemText;
    
    // Add delete button
    let deleteButton = document.createElement('button');
    deleteButton.textContent = 'Delete';
    deleteButton.addEventListener('click', function() {
        itemList.removeChild(listItem);
    });
    
    listItem.appendChild(deleteButton);
    itemList.appendChild(listItem);
    
    // Clear input
    input.value = '';
}

// Event listeners
addButton.addEventListener('click', addItem);
input.addEventListener('keypress', function(event) {
    if (event.key === 'Enter') {
        addItem();
    }
});
\`\`\``,
      practice: {
        task: 'Create an interactive todo list application',
        starter: `// Todo List Application
// HTML structure:
// <input type="text" id="todo-input" placeholder="Enter a todo...">
// <button id="add-todo">Add Todo</button>
// <ul id="todo-list"></ul>

// Add your JavaScript code here to:
// 1. Add new todos
// 2. Mark todos as complete
// 3. Delete todos
// 4. Show todo count`,
        hints: [
          'Use document.querySelector to select elements',
          'Add event listeners for click and keypress events',
          'Create new elements dynamically with createElement',
          'Use classList.toggle for marking items complete',
          'Keep track of todo count and update display'
        ]
      }
    }
  ]
};
